<template>
  <div class="news-page">
    <!-- 顶部横幅部分 -->
    <div class="section-banner">
      <div class="content">
        <div class="search-container">
          <el-input 
            placeholder="输入关键词搜索" 
            class="search-input"
            v-model="searchQuery"
            @keyup.enter.native="handleSearch">
          </el-input>
          <el-button class="search-btn" type="warning" @click="handleSearch">搜索</el-button>
        </div>
      </div>
    </div>

    <!-- 筛选区域 -->
    <div class="filter-section">
      <div class="filter-container">
        <div class="filter-row">
          <div class="filter-label">所属地区:</div>
          <div class="filter-options">
            <div 
              v-for="(area, index) in areaList" 
              :key="area.code" 
              class="filter-option" 
              :class="{ active: selectedAreaCode === area.code }"
              @click="selectArea(area.code)">
              {{ area.name }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 新闻列表 -->
    <div class="news-container">
      <h2 class="section-title">产业动态</h2>
      <div class="news-list">
        <div class="news-item" v-for="(item, index) in newsList" :key="index">
          <div class="news-item-content">
            <div class="news-icon"></div>
            <div class="news-info">
              <h3 class="news-title">{{ item.title }}</h3>
              <p class="news-desc">{{ stripHtml(item.content) }}</p>
              <div class="news-date">{{ item.pubtime }}</div>
            </div>
          </div>
          <el-button class="view-btn" type="warning" @click="viewNewsDetail(item.id)">查看详情</el-button>
        </div>
      </div>
      
      <!-- 分页 -->
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pageNum"
        :page-sizes="[10, 20, 30, 50]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        class="pagination">
      </el-pagination>
    </div>
  </div>
</template>

<script>
import { getIndustryNewsListApi, areaListApi } from '@/api/index'

export default {
  name: 'newsPage',
  data() {
    return {
      // 筛选参数
      searchQuery: '',
      selectedAreaCode: '',
      pageNum: 1,
      pageSize: 10,
      total: 0,
      
      // 数据列表
      newsList: [],
      
      // 地区列表
      areaList: [
        { name: '全部', code: '' }
      ],
      
      loading: false
    }
  },
  created() {
    this.getAreaList() // 先获取地区列表
  },
  methods: {
    // 获取地区列表数据
    async getAreaList() {
      try {
        const res = await areaListApi();
        if (res && res.data) {
          // 只保留省级数据（即没有parent的数据）
          const provinceList = res.data.filter(item => !item.parentCode);
          
          // 添加到areaList，保留"全部"选项
          this.areaList = [{ name: '全部', code: '' }, ...provinceList.map(item => ({
            name: item.label,
            code: item.code
          }))];
          
          // 获取新闻列表
          this.getNewsList();
        } else {
          this.$message.error('获取地区列表失败');
          // 如果获取地区失败，仍然获取新闻列表
          this.getNewsList();
        }
      } catch (error) {
        console.error('获取地区列表出错', error);
        this.$message.error('获取地区列表出错');
        // 如果获取地区出错，仍然获取新闻列表
        this.getNewsList();
      }
    },
    
    // 去除HTML标签
    stripHtml(html) {
      if (!html) return '';
      
      // 先移除style标签及其内容
      let result = html.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
      
      // 移除所有HTML标签
      result = result.replace(/<\/?[^>]+(>|$)/g, '');
      
      // 处理HTML实体编码
      result = result.replace(/&nbsp;/g, ' ')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
      
      // 移除多余空白
      result = result.replace(/\s+/g, ' ').trim();
      
      // 限制字符数量，防止过长
      if (result.length > 100) {
        result = result.substring(0, 100) + '...';
      }
      
      return result;
    },
    
    // 获取新闻列表
    async getNewsList() {
      try {
        this.loading = true
        const params = {
          page: this.pageNum,
          limit: this.pageSize,
          areaCode: this.selectedAreaCode,
          keyWord: this.searchQuery
        }
        
        const res = await getIndustryNewsListApi(params)
        if (res.data.records) {
          this.newsList = res.data.records || []
          this.total = res.data.total || 0
        } else {
          this.$message.error('获取新闻列表失败')
        }
      } catch (error) {
        console.error('获取新闻列表出错', error)
        this.$message.error('获取新闻列表出错')
      } finally {
        this.loading = false
      }
    },
    
    // 查看新闻详情
    viewNewsDetail(id) {
      // 使用window.open在新标签页打开详情页
      const detailUrl = `${window.location.origin}/news/detail/${id}`;
      window.open(detailUrl, '_blank');
    },
    
    // 选择地区
    selectArea(code) {
      this.selectedAreaCode = code
      this.pageNum = 1 // 重置页码
      this.getNewsList()
    },
    
    // 搜索
    handleSearch() {
      this.pageNum = 1 // 重置页码
      this.getNewsList()
    },
    
    // 改变每页条数
    handleSizeChange(val) {
      this.pageSize = val
      this.getNewsList()
    },
    
    // 改变页码
    handleCurrentChange(val) {
      this.pageNum = val
      this.getNewsList()
    }
  }
}
</script>

<style scoped lang="scss">
.news-page {
  width: 100%;
  background-color: #fff;
  color: #333;
}

// 顶部横幅
.section-banner {
  height: 1080px;
  background-image: url('https://echance.oss-cn-beijing.aliyuncs.com/news-bg.png');
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;

  .content {
    width: 1200px;
    margin: 0 auto;

    .search-container {
      width: 600px;
      margin: 0 auto;
      display: flex;

      .search-input {
        flex: 1;

        ::v-deep .el-input__inner {
          height: 50px;
          border-radius: 5px 0 0 5px;
          border: none;
          font-size: 18px;
          opacity: 0.9;
        }
      }

      .search-btn {
        width: 80px;
        height: 50px;
        border-radius: 0 5px 5px 0;
        background-color: #FFC107;
        border-color: #FFC107;
        color: #000;
        font-size: 18px;
        margin-left: -1px;
      }
    }
  }
}

// 筛选区域
.filter-section {
  background: #fff;
  padding: 20px;
  border-bottom: 1px solid #eee;
  width: 1240px;
  align-items: center;
  margin: -115px auto 0;

  .filter-container {
    margin: 0 auto;
    background-color: #fff;
    border-radius: 5px;
  }

  .filter-row {
    display: flex;
    padding: 10px 0;

    .filter-label {
      margin-right: 24px;
      font-size: 24px;
      color: #00008E;
      line-height: 22px;
      font-weight: bold;
      padding-top: 5px;
    }

    .filter-options {
      flex: 1;
      display: flex;
      flex-wrap: wrap;

      .filter-option {
        font-size: 22px;
        color: #666666;
        line-height: 22px;
        cursor: pointer;
        margin: 0 24px 20px 0;
        align-content: center;

        &.active {
          color: #00008E;
          font-weight: bold;
          background: #FFCC29;
          padding: 5px;
          box-sizing: border-box;
        }

        &:hover {
          color: #FFC107;
        }
      }
    }
  }
}

// 新闻列表
.news-container {
  width: 1200px;
  margin: 40px auto 80px;

  .section-title {
    font-weight: bold;
    font-size: 64px;
    color: #19456B;
    line-height: 52px;
    margin-bottom: 60px;
    text-align: center;
  }

  .news-list {
    display: flex;
    flex-direction: column;

    .news-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 35px;
      border-bottom: 1px solid #eee;
      background: #FFFFFF;
    box-shadow: 0px 2px 25px 0px rgba(0,0,142,0.15);
    margin-bottom: 24px;

      .news-item-content {
        display: flex;
        flex: 1;

        .news-icon {
          width: 8px;
          height: 31px;
          background: #FFCC29;
          border-radius: 8px;
          margin-right: 12px;
        }

        .news-info {
          flex: 1;
          margin-right: 70px;

          .news-title {
            font-size: 32px;
            color: #19456B;
            line-height: 30px;
            font-weight: bold;
            margin-bottom: 20px;
          }

          .news-desc {
            font-size: 16px;
            color: #999999;
            line-height: 24px;
            margin-bottom: 16px;
          }

          .news-date {
            font-size: 14px;
            color: #999;
          }
        }
      }

      .view-btn {
        width: 190px;
        background-color: #FFC107;
        border-color: #FFC107;
        font-size: 18px;
        color: #0F0FB1;
        line-height: 22px;
        padding: 8px 15px;
      }
    }
  }
}

// 添加分页样式
.pagination {
  margin-top: 40px;
  text-align: center;
}
</style>